import { useState, useRef, memo, useCallback } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Stack, Typography, useTheme, Collapse, Grid } from "@mui/material";
import { NativePuckTitleField } from "./NativePuckTitleField";

const PUCK_CONFIG = {
    FontFamily: "var(--puck-font-family) !important",
};

export type ColorPickerFieldProps = {
    onChange: (value: string) => void;
    value?: string;
    label?: string;
};

export const ColorPickerField = (props: ColorPickerFieldProps) => {
    const { onChange, value, label = "Value color" } = props;

    const [showPicker, setShowPicker] = useState(false);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const theme = useTheme();
    const defaultColor = theme.palette.common.black;
    const currentColor = value || defaultColor;

    const DEBOUNCE_COLOR_CHANGE_DELAY = 60;

    const handleColorChange = useCallback(
        (color: string) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                onChange(color);
            }, DEBOUNCE_COLOR_CHANGE_DELAY);
        },
        [onChange],
    );

    const colorPalettes = {
        app: [
            theme.palette.primary.main,
            theme.palette.primary.light,
            theme.palette.primary.dark,
            theme.palette.secondary.main,
            theme.palette.secondary.light,
            theme.palette.secondary.dark,
        ],
        feedback: [
            theme.palette.success.main,
            theme.palette.info.main,
            theme.palette.warning.main,
            theme.palette.error.main,
        ],
        dataviz: Object.values(theme.custom.dataviz.colorsWithExtra).map((color) => color.main),
        grays: [
            theme.palette.grey[900],
            theme.palette.grey[800],
            theme.palette.grey[700],
            theme.palette.grey[600],
            theme.palette.grey[500],
            theme.palette.grey[400],
            theme.palette.grey[300],
            theme.palette.grey[200],
            theme.palette.grey[100],
            theme.palette.grey[50],
        ],
    };

    const handleColorBoxClick = useCallback(
        (color: string) => {
            handleColorChange(color);
            setShowPicker(false);
        },
        [handleColorChange],
    );

    const ColorBoxComponent = ({ color }: { color: string }) => (
        <Box
            onClick={() => handleColorBoxClick(color)}
            sx={{
                width: theme.spacing(3),
                height: theme.spacing(3),
                bgcolor: color,
                borderRadius: theme.spacing(0.75),
                cursor: "pointer",
                boxShadow: currentColor === color ? "0 2px 4px rgba(0, 0, 0, 0.3)" : "none",
                border:
                    currentColor === color ? `2px solid ${theme.palette.common.black}` : `1px solid rgba(0,0,0,0.12)`,
                transition: "transform 0.1s ease",
                "&:hover": {
                    transform: "scale(1.1)",
                },
            }}
        />
    );

    const ColorBox = memo(ColorBoxComponent);
    ColorBox.displayName = "ColorBox";

    const SectionTitle = ({ title }: { title: string }) => (
        <Typography
            variant="caption"
            sx={{
                color: theme.custom.grey[1400],
                fontFamily: PUCK_CONFIG.FontFamily,
                display: "block",
                mb: 1,
            }}
        >
            {title}
        </Typography>
    );

    return (
        <Box>
            <NativePuckTitleField label={label} icon="edit" />
            <Box sx={{ width: "100%" }}>
                <Box
                    onClick={() => setShowPicker(!showPicker)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: theme.spacing(1),
                        cursor: "pointer",
                    }}
                >
                    <Box
                        sx={{
                            width: theme.spacing(3),
                            height: theme.spacing(3),
                            borderRadius: theme.spacing(0.75),
                            backgroundColor: currentColor,
                            border: `1px solid rgba(0, 0, 0, 0.12)`,
                            flexShrink: 0,
                            transition: "transform 0.2s ease",
                            "&:hover": {
                                transform: "scale(1.05)",
                            },
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: "rgba(0, 0, 0, 0.6)",
                            fontFamily: PUCK_CONFIG.FontFamily,
                        }}
                    >
                        {currentColor.toUpperCase()}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />

                    <Box
                        sx={{
                            width: theme.spacing(3),
                            height: theme.spacing(3),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "transform 0.2s ease",
                            transform: showPicker ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                    >
                        <Box sx={{ color: "rgba(0, 0, 0, 0.4)" }}>
                            <KeyboardArrowDownIcon fontSize="small" />
                        </Box>
                    </Box>
                </Box>

                <Collapse in={showPicker}>
                    <Box
                        sx={{
                            width: "100%",
                            p: theme.spacing(1.5),
                            bgcolor: "#ffffff",
                            borderRadius: theme.spacing(0.75),
                            border: `1px solid rgba(0, 0, 0, 0.08)`,
                            my: theme.spacing(1),
                        }}
                    >
                        <Box sx={{ mb: theme.spacing(2) }}>
                            <SectionTitle title="App Colors" />
                            <Grid container spacing={1}>
                                {colorPalettes.app.map((color) => (
                                    <Grid item key={color}>
                                        <ColorBox color={color} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ mb: theme.spacing(2) }}>
                            <SectionTitle title="Feedback Colors" />
                            <Grid container spacing={1}>
                                {colorPalettes.feedback.map((color) => (
                                    <Grid item key={color}>
                                        <ColorBox color={color} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ mb: theme.spacing(2) }}>
                            <SectionTitle title="Data Visualization" />
                            <Grid container spacing={1}>
                                {colorPalettes.dataviz.map((color) => (
                                    <Grid item key={color}>
                                        <ColorBox color={color} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ mb: theme.spacing(2) }}>
                            <SectionTitle title="Grayscale" />
                            <Grid container spacing={1}>
                                {colorPalettes.grays.map((color) => (
                                    <Grid item key={color}>
                                        <ColorBox color={color} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box
                            sx={{
                                mt: theme.spacing(2),
                                pt: theme.spacing(2),
                                borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                            }}
                        >
                            <SectionTitle title="Custom Color" />
                            <Stack
                                sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: theme.spacing(1.5),
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: theme.spacing(3),
                                        height: theme.spacing(3),
                                        borderRadius: theme.spacing(0.75),
                                        backgroundColor: currentColor,
                                        border: `1px solid rgba(0, 0, 0, 0.12)`,
                                        cursor: "pointer",
                                        transition: "transform 0.1s ease",
                                        overflow: "hidden",
                                        "&:hover": {
                                            transform: "scale(1.1)",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                        },
                                    }}
                                >
                                    <input
                                        type="color"
                                        value={currentColor}
                                        onChange={(e) => handleColorChange(e.target.value)}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "150%",
                                            height: "150%",
                                            WebkitAppearance: "none",
                                            MozAppearance: "none",
                                            appearance: "none",
                                            backgroundColor: "transparent",
                                            border: "none",
                                            padding: 0,
                                            margin: "-25%",
                                            cursor: "pointer",
                                            opacity: 0,
                                        }}
                                    />
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        component="span"
                                        sx={{
                                            fontFamily: PUCK_CONFIG.FontFamily,
                                        }}
                                    >
                                        {currentColor.toUpperCase()}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
};

export default ColorPickerField;
